import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import store from './store';
import DropDownItem from './DropDownItem';
import STATES from './STATES';
import InputType from './InputType';
import theme from '../../theme';

const isBrowser = typeof window !== 'undefined';
const offset = isBrowser ? require('document-offset') : () => ({ top: null, left: null }); // eslint-disable-line
const closest = isBrowser ? require('element-closest') : null;  // eslint-disable-line

const Menu = styled.ul`
  margin: 0;
  padding: 0;
`;

const DropDownLayout = styled.div`
  position: relative;
`;

const dropdown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DropDownContainer = styled.div`
  animation: ${dropdown} 150ms ease-out forwards 0s;
  position: absolute;
  z-index: 1000;
`;

const DropDown = styled.div`
  background: ${theme.colors.white};
  border: solid 1px ${theme.colors.gray16};
  border-radius: ${theme.unit}px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
  margin: ${theme.unit}px 0;
  min-width: ${theme.unit * 28}px;
  position: absolute;
`;

const ArrowIcon = styled.i`
  background: ${theme.colors.white};
  border: solid 1px ${theme.colors.gray16};
  border-bottom-width: 0;
  border-right-width: 0;
  height: ${theme.unit}px;
  position: absolute;
  transform: rotate(45deg);
  width: ${theme.unit}px;
  z-index: -1;
`;

const Buttons = styled.div`
  border-bottom: solid 1px ${theme.colors.gray16};
  padding: ${theme.unit / 2}px;

  &:last-child {
    border-bottom: 0;
  }

  li {
    border-radius: ${theme.unit / 2}px;
    box-sizing: border-box;
    color: ${theme.colors.action};
    cursor: pointer;
    display: block;
    height: ${theme.unit * 4}px;
    line-height: ${theme.unit * 4}px;
    margin: ${theme.unit / 2}px 0;
    padding: 0 ${theme.unit}px;
    width: 100%;

    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-top: 0;
    }

    &:hover {
      background: ${theme.colors.action};
      color: ${theme.colors.white};
    }

    & > * {
      display: block;
      width: 100%;
      height: 100%;
      line-height: inherit;

      div {
        line-height: 1.5;
      }
    }
  }

  ${({ type }) => type === 'info' && `
    li {
      color: ${theme.colors.gray1};
      cursor: default;
      height: auto;

      &:hover {
        background: inherit;
        color: ${theme.colors.gray1};
      }

      a:hover {
        margin: 0 -${theme.unit}px;
        padding: 0 ${theme.unit}px;
        background: ${theme.colors.gray19};
        border-radius: ${theme.unit / 2}px;
      }
    }
  `}
  ${({ type }) => type === 'destructive' && `
    li {
      color: ${theme.colors.destructive};

      &:hover {
        background: ${theme.colors.destructive};
        color: ${theme.colors.white};
      }
    }
  `}
`;

class DropDownMenu extends React.PureComponent {
  static propTypes = {
    open: PropTypes.bool,
    options: InputType,
    top: PropTypes.number,
    left: PropTypes.number,
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
    showArrow: PropTypes.bool,
    anchorWidth: PropTypes.number,
  }

  static defaultProps = {
    open: false,
    options: [],
    top: 0,
    left: 0,
    vertical: 'top',
    horizontal: 'left',
    showArrow: false,
    anchorWidth: 0,
  }

  static open(e, options, {
    anchor,
    showArrow,
    align,
  }) {
    e.stopPropagation();
    // eslint-disable-next-line
    const node = ReactDOM.findDOMNode(anchor);
    const position = offset(node);
    const layout = document.querySelector('#dropdown');
    const layoutPosition = offset(layout);
    const anchorHeight = node.clientHeight;
    const anchorWidth = node.clientWidth;
    const scrollHeight = document.body.scrollHeight;
    const scrollWidth = document.body.scrollWidth;
    let top = (position.top - layoutPosition.top) + anchorHeight + (showArrow ? 8 : 0);
    let vertical = 'top';
    let left = position.left + layoutPosition.left;
    let horizontal = 'left';
    const groupCount = options.length;
    const optionCount = options.reduce((acc, a) => acc + a.options.length, 0);
    const potentialHeight = (groupCount * 4 * 2) // group padding
      + (optionCount * 32) // option height
      + ((optionCount - 1) * 4) // margins between options
      + ((showArrow ? 16 : 8) * 2); // margins on the menu
    const potentialWidth = 200;
    if (align === 'left' || position.left < scrollWidth - (potentialWidth * 1.25)) {
      left = position.left + layoutPosition.left;
      horizontal = 'left';
    }
    if (align === 'right' || position.left > potentialWidth * 1.25) {
      left = position.left + layoutPosition.left + anchorWidth;
      horizontal = 'right';
    }
    if (align === 'center' && (
      position.left < scrollWidth - ((potentialWidth * 1.5) / 2)
    ) && (
      position.left > (potentialWidth * 1.5) / 2
    )) {
      left = position.left + layoutPosition.left + (anchorWidth / 2);
      horizontal = 'center';
    }
    if (position.top > scrollHeight - (potentialHeight * 1.25)) {
      top -= anchorHeight + (showArrow ? 24 : 8);
      vertical = 'bottom';
    }
    const props = {
      options,
      top,
      left,
      vertical,
      horizontal,
      anchor: node,
      anchorWidth,
      showArrow,
    };
    PropTypes.checkPropTypes(DropDownMenu.propTypes, props);
    store.dispatch({
      type: STATES.OPEN,
      ...props,
    });
  }

  static close() {
    store.dispatch({
      type: STATES.CLOSE,
    });
  }

  componentDidMount() {
    this.handleClick = ({ target }) => {
      if (!target.closest('#dropdown')) {
        store.dispatch({ type: STATES.CLOSE });
      }
    };
    this.handleResize = () => {
      store.dispatch({ type: STATES.CLOSE });
    };
    this.handleHistoryChange = () => {
      store.dispatch({ type: STATES.CLOSE });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('popstate', this.handleHistoryChange);
      window.addEventListener('pushstate', this.handleHistoryChange);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.open && this.props.anchor) {
      this.props.anchor.blur();
    }
    if (nextProps.open && nextProps.anchor) {
      nextProps.anchor.focus();
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);
      window.addEventListener('popstate', this.handleHistoryChange);
      window.removeEventListener('pushstate', this.handleHistoryChange);
    }
  }

  render() {
    if (!this.props.open) {
      return <DropDownLayout id="dropdown" />;
    }

    return (
      <DropDownLayout id="dropdown">
        {this.props.open && (
          <DropDownContainer>
            <DropDown
              style={{
                top: this.props.top,
                left: this.props.left,
                transform: `translate(${
                  this.props.horizontal === 'left' ? '0' : ( // eslint-disable-line
                    this.props.horizontal === 'center' ? '-50%' : '-100%'
                  )
                }, ${
                  this.props.vertical === 'top' ? '0' : '-100%'
                })`,
              }}
            >
              {this.props.showArrow && this.props.anchorWidth && (
                <ArrowIcon
                  style={{
                    ...this.props.vertical === 'top' && {
                      top: -6,
                      bottom: 'auto',
                    },
                    ...this.props.vertical === 'bottom' && {
                      top: 'auto',
                      bottom: -6,
                      borderTopWidth: 0,
                      borderLeftWidth: 0,
                      borderBottomWidth: 1,
                      borderRightWidth: 1,
                    },
                    ...this.props.horizontal === 'left' && {
                      left: this.props.anchorWidth / 2,
                      right: 'auto',
                      marginRight: 0,
                    },
                    ...this.props.horizontal === 'center' && {
                      left: 'auto',
                      right: '50%',
                      marginRight: this.anchorWidth ? this.anchorWidth / 2 : 0,
                    },
                    ...this.props.horizontal === 'right' && {
                      left: 'auto',
                      right: this.props.anchorWidth / 2,
                      marginRight: 0,
                    },
                  }}
                />
              )}
              {this.props.options.map(({ _id, type, options }) => (
                <Buttons
                  key={_id}
                  type={type}
                >
                  <Menu>
                    {options.map(option =>
                      <DropDownItem
                        key={option._id}
                        {...option}
                        onClick={(...props) => {
                          if (option.onClick) {
                            option.onClick(...props);
                          }
                          if (option.onClick || option.to) {
                            store.dispatch({ type: STATES.CLOSE });
                          }
                        }}
                      />,
                    )}
                  </Menu>
                </Buttons>
              ))}
            </DropDown>
          </DropDownContainer>
        )}
      </DropDownLayout>
    );
  }
}

export default compose(
  connect(() => ({ store })),
  connect(state => state),
)(DropDownMenu);



// WEBPACK FOOTER //
// ./src/components/stateful/DropDown/DropDownMenu.js